import {
    CheckCircleOutline,
    Delete,
    Edit,
    ErrorOutline,
    PlayArrow,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxTable, { IAcxTableColumn } from "components/UI/AcxTable/AcxTable";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { observer } from "mobx-react";
import type { LLMHostViewModel } from "models/LLMs/LLMHostViewModel";
import type { TestResultViewModel } from "models/LLMs/TestResultViewModel";
import React, { useMemo, useState } from "react";
import type LLMManagementStore from "stores/LLMManagementStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import AddButton from "../OrganizationDataSources/components/AddButton";

const HOST_TYPE_OPTIONS = [
    { value: "AzureOpenAI", label: "Azure OpenAI" },
    { value: "AcxSelfHosted", label: "ACX Self Hosted" },
];

interface HostFormState {
    name: string;
    type: string;
    endpointUrl: string;
    keyVaultSecretNameForApiKey: string;
}

const emptyForm: HostFormState = {
    name: "",
    type: "AzureOpenAI",
    endpointUrl: "",
    keyVaultSecretNameForApiKey: "",
};

function formFromHost(host: LLMHostViewModel): HostFormState {
    return {
        name: host.name,
        type: host.type,
        endpointUrl: String(
            host.configuration?.EndpointUrl ??
                host.configuration?.endpointUrl ??
                "",
        ),
        keyVaultSecretNameForApiKey: String(
            host.configuration?.KeyVaultSecretNameForApiKey ??
                host.configuration?.keyVaultSecretNameForApiKey ??
                "",
        ),
    };
}

function formToPayload(
    form: HostFormState,
): Omit<LLMHostViewModel, "id" | "isActive"> {
    const configuration: Record<string, unknown> = {
        EndpointUrl: form.endpointUrl,
    };
    if (form.type === "AzureOpenAI") {
        configuration.KeyVaultSecretNameForApiKey =
            form.keyVaultSecretNameForApiKey;
    }
    return {
        name: form.name,
        type: form.type,
        configuration,
    };
}

interface Props {
    store: LLMManagementStore;
}

const HostsTable: React.FC<Props> = observer(({ store }) => {
    const messageStore = useStore(MessageStore);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingHost, setEditingHost] = useState<LLMHostViewModel | null>(
        null,
    );
    const [form, setForm] = useState<HostFormState>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [testResults, setTestResults] = useState<
        Record<string, TestResultViewModel>
    >({});
    const [testingId, setTestingId] = useState<string | null>(null);
    const [deleteConfirmHost, setDeleteConfirmHost] =
        useState<LLMHostViewModel | null>(null);

    const openCreate = () => {
        setEditingHost(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (host: LLMHostViewModel) => {
        setEditingHost(host);
        setForm(formFromHost(host));
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = formToPayload(form);
            if (editingHost) {
                await store.updateHost(editingHost.id, payload);
                messageStore.logMessage("Host updated", "success");
            } else {
                await store.createHost(payload);
                messageStore.logMessage("Host created", "success");
            }
            setDialogOpen(false);
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await store.deleteHost(id);
            messageStore.logMessage("Host deleted", "success");
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setDeleteConfirmHost(null);
        }
    };

    const handleTest = async (id: string) => {
        setTestingId(id);
        try {
            const result = await store.testHost(id);
            setTestResults((prev) => ({ ...prev, [id]: result }));
        } catch {
            setTestResults((prev) => ({
                ...prev,
                [id]: {
                    success: false,
                    message: "Test request failed",
                    latencyMs: null,
                },
            }));
        } finally {
            setTestingId(null);
        }
    };

    const columns: IAcxTableColumn[] = useMemo(
        () => [
            {
                headerLabel: "Name",
                dataKey: "name",
            },
            {
                headerLabel: "Type",
                dataKey: "type",
            },
            {
                headerLabel: "Endpoint URL",
                dataKey: "configuration.EndpointUrl",
                formatter: (_value, row: LLMHostViewModel) =>
                    String(
                        row.configuration?.EndpointUrl ??
                            row.configuration?.endpointUrl ??
                            "",
                    ),
            },
            {
                headerLabel: "Test Result",
                dataKey: "testResult",
                formatter: (_value, row: LLMHostViewModel) => {
                    const result = testResults[row.id];
                    if (!result) return <></>;
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                height: "100%",
                            }}
                        >
                            {result.success ? (
                                <CheckCircleOutline
                                    color="success"
                                    fontSize="small"
                                />
                            ) : (
                                <ErrorOutline color="error" fontSize="small" />
                            )}
                            <Typography variant="caption" noWrap>
                                {result.message}
                                {result.latencyMs != null &&
                                    ` (${result.latencyMs}ms)`}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                headerLabel: "Actions",
                dataKey: "actions",
                formatter: (_value, row: LLMHostViewModel) => (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                            width: "100%",
                        }}
                    >
                        <Tooltip title="Test connection">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleTest(row.id)}
                                    disabled={testingId === row.id}
                                >
                                    <PlayArrow fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => openEdit(row)}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => setDeleteConfirmHost(row)}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [testResults, testingId],
    );

    const canSave = form.name.trim() !== "" && form.endpointUrl.trim() !== "";

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                }}
            >
                <AddButton onClick={openCreate}>Host</AddButton>
            </Box>

            <AcxTable
                columns={columns}
                rows={store.hosts}
                enableHover
                cellDense
            />

            {/* Create/Edit Dialog */}
            <AcxDialog
                title={editingHost ? "Edit Host" : "Add Host"}
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth={false}
                contentWidth="min(720px, calc(100vw - 96px))"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <AcxMainTextField
                            id="host-name"
                            labelText="Name"
                            required
                            fullWidth
                            value={form.name}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    name: e.target.value,
                                }))
                            }
                        />
                        <Box>
                            <AcxSelect
                                id="host-type"
                                inputLabel="Type"
                                fullWidth
                                options={HOST_TYPE_OPTIONS}
                                defaultValue={HOST_TYPE_OPTIONS.find(
                                    (o) => o.value === form.type,
                                )}
                                onChange={(opt) =>
                                    setForm((f) => ({
                                        ...f,
                                        type: opt?.value ?? f.type,
                                    }))
                                }
                            />
                        </Box>
                        <AcxMainTextField
                            id="host-endpoint-url"
                            labelText="Endpoint URL"
                            required
                            fullWidth
                            value={form.endpointUrl}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    endpointUrl: e.target.value,
                                }))
                            }
                        />
                        {form.type === "AzureOpenAI" && (
                            <AcxMainTextField
                                id="host-key-vault-secret"
                                labelText="Key Vault Secret Name for API Key"
                                fullWidth
                                value={form.keyVaultSecretNameForApiKey}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        keyVaultSecretNameForApiKey:
                                            e.target.value,
                                    }))
                                }
                            />
                        )}
                    </Box>
                }
            >
                <AcxButton
                    color="primary"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!canSave}
                >
                    {editingHost ? "Update" : "Create"}
                </AcxButton>
            </AcxDialog>

            {/* Delete Confirmation Dialog */}
            <AcxDialog
                title="Delete Host"
                text="Are you sure you want to delete this host? Hosts with active deployments cannot be deleted."
                isOpen={deleteConfirmHost !== null}
                onClose={() => setDeleteConfirmHost(null)}
                contentWidth="400px"
                dialogContentChildren={
                    deleteConfirmHost && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                                fontWeight: 600,
                            }}
                        >
                            - {deleteConfirmHost.name}
                        </Typography>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() =>
                        deleteConfirmHost && handleDelete(deleteConfirmHost.id)
                    }
                >
                    Delete
                </AcxButton>
            </AcxDialog>
        </Box>
    );
});

export default HostsTable;
